/**
 * Super Admin - Users Management
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/Toast/useToast';

export function SuperAdminUsers() {
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, super_admin, company_admin, company_user

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load companies first
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesMap = {};
      companiesSnapshot.docs.forEach(doc => {
        companiesMap[doc.id] = { id: doc.id, ...doc.data() };
      });
      setCompanies(companiesMap);

      // Load users
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast({ type: 'error', message: 'Error al cargar usuarios' });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    return user.role === filter;
  });

  const getRoleBadge = (role) => {
    const badges = {
      'super_admin': { label: '👑 Super Admin', color: '#9333ea' },
      'company_admin': { label: '🏢 Admin Empresa', color: '#2563eb' },
      'company_user': { label: '👤 Usuario', color: '#059669' }
    };
    return badges[role] || { label: role, color: '#6b7280' };
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <p className="meta">Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Usuarios</h1>
          <p className="meta">Gestión de todos los usuarios del sistema</p>
        </div>
        <Link to="/super-admin/users/new" className="btn">
          ➕ Crear Usuario
        </Link>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'btn' : 'copy'}
            style={{ fontSize: '14px' }}
          >
            Todos ({users.length})
          </button>
          <button
            onClick={() => setFilter('super_admin')}
            className={filter === 'super_admin' ? 'btn' : 'copy'}
            style={{ fontSize: '14px' }}
          >
            👑 Super Admins ({users.filter(u => u.role === 'super_admin').length})
          </button>
          <button
            onClick={() => setFilter('company_admin')}
            className={filter === 'company_admin' ? 'btn' : 'copy'}
            style={{ fontSize: '14px' }}
          >
            🏢 Admins ({users.filter(u => u.role === 'company_admin').length})
          </button>
          <button
            onClick={() => setFilter('company_user')}
            className={filter === 'company_user' ? 'btn' : 'copy'}
            style={{ fontSize: '14px' }}
          >
            👤 Usuarios ({users.filter(u => u.role === 'company_user').length})
          </button>
        </div>
      </div>

      {/* Users List */}
      <div className="grid" style={{ gap: '12px' }}>
        {filteredUsers.length === 0 ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
            <p className="meta">No hay usuarios que mostrar</p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const roleBadge = getRoleBadge(user.role);
            const company = user.companyId ? companies[user.companyId] : null;

            return (
              <div key={user.id} className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>
                      {user.name || 'Sin nombre'}
                    </h3>
                    <p className="meta" style={{ marginBottom: '8px' }}>
                      📧 {user.email}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span 
                        className="badge" 
                        style={{ 
                          background: roleBadge.color,
                          fontSize: '12px'
                        }}
                      >
                        {roleBadge.label}
                      </span>
                      {company && (
                        <span className="badge" style={{ fontSize: '12px' }}>
                          🏢 {company.name}
                        </span>
                      )}
                      {user.active ? (
                        <span className="badge" style={{ background: '#059669', fontSize: '12px' }}>
                          ✅ Activo
                        </span>
                      ) : (
                        <span className="badge" style={{ background: '#dc2626', fontSize: '12px' }}>
                          ⏸️ Inactivo
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Link 
                      to={`/super-admin/users/${user.id}`}
                      className="copy"
                    >
                      Ver detalles
                    </Link>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SuperAdminUsers;
